import type { MiloRef } from './stream';

export type Inline = { kind: 'text' | 'bold'; text: string } | { kind: 'link'; text: string; path: string };
export type Block = { kind: 'paragraph'; inlines: Inline[] } | { kind: 'bullets'; items: Inline[][] };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The two pages an answer may open, the same rule as echo's milo/refs.ts */
export function allowedRefPath(path: string): boolean {
  const match = /^\/(loads|trucks)\?(load|truck)=([^&#\s]+)$/.exec(path);
  if (!match) return false;
  const [, page, key, id] = match;
  return ((page === 'loads' && key === 'load') || (page === 'trucks' && key === 'truck')) && UUID.test(id);
}

const TOKEN = /\*\*([^*\n]+)\*\*|\[([^\]\n]+)\]\((r\d+)\)/g;

function inlinesOf(text: string, refs: Record<string, MiloRef>): Inline[] {
  const inlines: Inline[] = [];
  let last = 0;
  for (const match of text.matchAll(TOKEN)) {
    const at = match.index ?? 0;
    if (at > last) inlines.push({ kind: 'text', text: text.slice(last, at) });
    if (match[1] !== undefined) {
      inlines.push({ kind: 'bold', text: match[1] });
    } else {
      const ref = refs[match[3]];
      inlines.push(ref && allowedRefPath(ref.path) ? { kind: 'link', text: match[2], path: ref.path } : { kind: 'text', text: match[2] });
    }
    last = at + match[0].length;
  }
  if (last < text.length) inlines.push({ kind: 'text', text: text.slice(last) });
  return inlines;
}

/** The answer's text as paragraphs and "- " bullets, with **bold** and [label](rN) links to known, allowed refs */
export function parseAnswer(text: string, refs: Record<string, MiloRef>): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let bullets: Inline[][] = [];
  const flush = () => {
    if (paragraph.length > 0) blocks.push({ kind: 'paragraph', inlines: inlinesOf(paragraph.join(' '), refs) });
    if (bullets.length > 0) blocks.push({ kind: 'bullets', items: bullets });
    paragraph = [];
    bullets = [];
  };
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    const bullet = /^[-•]\s+(.*)$/.exec(line);
    if (line === '') {
      flush();
    } else if (bullet) {
      if (paragraph.length > 0) flush();
      bullets.push(inlinesOf(bullet[1], refs));
    } else {
      if (bullets.length > 0) flush();
      paragraph.push(line);
    }
  }
  flush();
  return blocks;
}
